import { useMemo } from 'react';

import type { SelectOptionT } from '@/components/Select/types';
import Typo from '@/components/Typography/Typo';
import useAgentStore from '@/features/Agent/stores';
import TagSelectorOnRow from '@/features/Commons/CustomCellItem/TagSelectorOnRow';
import AttrSelectDropdown from '@/features/Commons/Dropdown/AttrSelectDropdown';
import useColor from '@/hooks/domain/useColor';
import useColumnConfigStore from '@/stores/DataTable/ColumnConfigStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { EllipsisDiv } from '@/styles/style';
import { FILTER_OPERATOR } from '@/types/dataTableConst';
import { AGENT_COLUMNS } from '@/types/filterKeyConst';
import { DEFAULT_HIGHLIGHT_TARGET } from '@/utils/util';

const useAgentHeader = () => {
  const { agent_view } = useColumnConfigStore((state) => state.columnConfigs);
  const { role_values, team_values } = useWorkspaceStore(
    (state) => state.workspaceFieldOptions,
  );

  const roleOptions: SelectOptionT[] = useMemo(() => {
    return role_values.map((value) => ({
      value,
      searchName: value,
    }));
  }, [role_values]);

  const teamOptions: SelectOptionT[] = useMemo(() => {
    return team_values.map((value) => ({
      value,
      searchName: value,
    }));
  }, [team_values]);

  const defaultColumns: DataTableColumnT[] = useMemo(() => {
    return [
      {
        columnKey: 'space',
        columnName: 'Space',
        hiddenColumnName: true,
        isDefaultColumn: true,
        width: 16,
        visible: true,
      },
      {
        columnKey: AGENT_COLUMNS.NAME,
        columnName: 'Name',
        isDefaultColumn: true,
        orderType: 'string' as OrderTypeT,
        sortKey: AGENT_COLUMNS.NAME,
        orderable: true,
        width: 300,
        visible: true,
        sort: (a, b) => {
          const aStr = a.rawData as string;
          const bStr = b.rawData as string;
          return bStr.localeCompare(aStr);
        },
      },
      {
        columnKey: AGENT_COLUMNS.ROLE,
        columnName: 'Role',
        width: 200,
        visible: false,
        filterable: true,
        searchable: true,
        filterList: roleOptions.map((role) => ({
          columnKey: AGENT_COLUMNS.ROLE,
          filterKey: role.value.toString(),
          filterName: role.value as string,
          filterOptionNode: role.value,
        })),
      },
      {
        columnKey: AGENT_COLUMNS.TEAM,
        columnName: 'Team',
        width: 200,
        visible: false,
        filterable: true,
        searchable: true,
        filterList: teamOptions.map((team) => ({
          columnKey: AGENT_COLUMNS.TEAM,
          filterKey: team.value.toString(),
          filterName: team.value as string,
          filterOptionNode: team.value,
        })),
      },
      {
        columnKey: AGENT_COLUMNS.EMAIL,
        columnName: 'Email',
        width: 200,
        visible: false,
      },
      {
        columnKey: AGENT_COLUMNS.LABEL,
        columnName: 'Tags',
        width: 200,
        visible: false,
      },
    ];
  }, [roleOptions, teamOptions]);

  const columns = useMemo(() => {
    const fullColumns = [...defaultColumns];
    agent_view.forEach((config) => {
      const targetIndex = fullColumns.findIndex(
        (col) => col.columnKey === config.name,
      );
      if (targetIndex === -1) return;
      fullColumns[targetIndex] = {
        ...fullColumns[targetIndex],
        width: config.width,
        visible: config.visible,
        columnOrderIndex: config.order,
      };
    });
    return fullColumns;
  }, [agent_view, defaultColumns]);

  return columns;
};

const useAgentTableData = (params: {
  searchValue: string;
  filterSetQuery: FilterSetQuery;
  sortStatus: SortStatusT;
}): DataTableRowT[] => {
  const { searchValue, filterSetQuery, sortStatus } = params;
  const color = useColor();
  const { agent_view } = useColumnConfigStore((state) => state.columnConfigs);
  const columnWidth = agent_view.find((c) => c.name === 'tags')?.width;

  const { agentMap, selectAgent, changeAgentAttr } = useAgentStore((state) => {
    return {
      agentMap: state.agentMap,
      selectAgent: state.selectAgent,
      changeAgentAttr: state.changeAgentAttr,
    };
  });

  const { workspaceFieldOptions, updateWorkspaceFieldOptions } =
    useWorkspaceStore((state) => {
      return {
        workspaceFieldOptions: state.workspaceFieldOptions,
        updateWorkspaceFieldOptions: state.updateWorkspaceFieldOptions,
      };
    });

  const roleOptions: SelectOptionT[] = useMemo(() => {
    return workspaceFieldOptions.role_values.map((value) => ({
      value,
      searchName: value,
    }));
  }, [workspaceFieldOptions.role_values]);

  const teamOptions: SelectOptionT[] = useMemo(() => {
    return workspaceFieldOptions.team_values.map((value) => ({
      value,
      searchName: value,
    }));
  }, [workspaceFieldOptions.team_values]);

  const result: DataTableRowT[] = useMemo(() => {
    return Object.values(agentMap)
      .filter((agent) => {
        let condition = true;
        for (const filter of filterSetQuery.filterQueryFormats) {
          const key =
            agentColumnMap[filter.columnKey] ||
            (filter.columnKey as keyof AgentT);
          const value = agent[key];
          if (filter.operator === FILTER_OPERATOR.OR) {
            condition = false;
            for (const filterValue of filter.filterValues) {
              if (value === filterValue.filterKey) {
                condition = true;
                break;
              }
            }
          } else {
            condition = true;
            for (const filterValue of filter.filterValues) {
              if (value !== filterValue.filterKey) {
                condition = false;
                break;
              }
            }
          }
        }
        if (
          !agent.email.includes(searchValue) &&
          !agent.name.includes(searchValue)
        ) {
          condition = false;
        }
        return condition;
      })
      .map((agent) => {
        return {
          onRowClick: () => selectAgent(agent.alias),
          rowKey: agent.alias,
          rowData: [
            {
              columnKey: 'space',
              children: <div />,
            },
            {
              columnKey: AGENT_COLUMNS.NAME,
              children: (
                <EllipsisDiv>
                  <Typo
                    type="label2"
                    color={color.gray700}
                    weight="m"
                    className={DEFAULT_HIGHLIGHT_TARGET}
                  >
                    {agent.name}
                  </Typo>
                </EllipsisDiv>
              ),
              onCellClick: () => selectAgent(agent.alias),
              rawData: agent.name,
            },
            {
              columnKey: AGENT_COLUMNS.ROLE,
              children: (
                <AttrSelectDropdown
                  name={`agent-list-${agent.alias}-role-dropdown`}
                  view="list"
                  selected={agent.role || null}
                  options={roleOptions}
                  onSelect={(key) =>
                    changeAgentAttr(agent.alias, 'role', key)
                  }
                  onClear={() =>
                    changeAgentAttr(agent.alias, 'role', undefined)
                  }
                  onCreate={async (key) => {
                    await updateWorkspaceFieldOptions(1, 'role', key);
                    changeAgentAttr(agent.alias, 'role', key);
                  }}
                  onDelete={(key) =>
                    updateWorkspaceFieldOptions(4, 'role', key.value as string)
                  }
                />
              ),
              rawData: agent.role,
            },
            {
              columnKey: AGENT_COLUMNS.TEAM,
              children: (
                <AttrSelectDropdown
                  name={`agent-list-${agent.alias}-team-dropdown`}
                  view="list"
                  selected={agent.team || null}
                  options={teamOptions}
                  onSelect={(key) =>
                    changeAgentAttr(agent.alias, 'team', key)
                  }
                  onClear={() =>
                    changeAgentAttr(agent.alias, 'team', undefined)
                  }
                  onCreate={async (key) => {
                    await updateWorkspaceFieldOptions(1, 'team', key);
                    changeAgentAttr(agent.alias, 'team', key);
                  }}
                  onDelete={(key) => {
                    updateWorkspaceFieldOptions(4, 'team', key.value as string);
                  }}
                />
              ),
              rawData: agent.team,
            },
            {
              columnKey: AGENT_COLUMNS.EMAIL,
              children: (
                <EllipsisDiv>
                  <Typo type="label2" color={color.gray700}>
                    {agent.email}
                  </Typo>
                </EllipsisDiv>
              ),
              rawData: agent.email,
            },
            {
              columnKey: AGENT_COLUMNS.LABEL,
              children: (
                <TagSelectorOnRow
                  tagList={agent.tags}
                  listStyle={{
                    maxWidth: 'auto',
                    overflow: 'hidden',
                    flexWrap: 'nowrap',
                  }}
                  columnWidth={columnWidth}
                />
              ),
              rawData: agent.last_activity_date,
            },
          ],
        };
      })
      .sort((a, b) => {
        const aStr = a.rowData.find(
          (col) => col.columnKey === sortStatus?.sortBy,
        )?.rawData;

        const bStr = b.rowData.find(
          (col) => col.columnKey === sortStatus?.sortBy,
        )?.rawData;

        if (!aStr || !bStr) return 0;

        if (sortStatus?.orderStatus === 'asc') {
          return aStr.localeCompare(bStr);
        } else {
          return bStr.localeCompare(aStr);
        }
      });
  }, [
    agentMap,
    changeAgentAttr,
    color.gray700,
    columnWidth,
    roleOptions,
    searchValue,
    selectAgent,
    teamOptions,
    JSON.stringify(filterSetQuery),
    updateWorkspaceFieldOptions,
    sortStatus?.sortBy,
    sortStatus?.orderStatus,
  ]);

  return result;
};

const agentColumnMap: Record<string, keyof AgentT> = {
  [AGENT_COLUMNS.ROLE]: 'role',
  [AGENT_COLUMNS.TEAM]: 'team',
};

export { useAgentHeader, useAgentTableData };
